import React, { useState, useEffect, useRef  } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DOMPurify from 'dompurify';

const Contact = () => {
    const [formData, setFormData] = useState({
      name: "",
      email: "",
      message: "",
    });
    const [loading, setLoading] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const getSize = () => (window.innerWidth < 400 ? "compact" : "normal");
    const [recaptchaSize, setRecaptchaSize] = useState(getSize());
    const [key, setKey] = useState(0);
    const recaptchaRef = useRef(null); 


  useEffect(() => {
    const handleResize = () => {
      const newSize = getSize();
      if (newSize !== recaptchaSize) {
        setRecaptchaSize(newSize);
        setKey((prevKey) => prevKey + 1);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [recaptchaSize]);
    
    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      };      

    const handleRecaptcha = (token) => {
        setRecaptchaToken(token);
      };  

    const handleChange = (e) => {
        const sanitizedValue = DOMPurify.sanitize(e.target.value);
        setFormData({ ...formData, [e.target.name]: sanitizedValue });
      };
  
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!/^[A-Za-z\s]{2,}$/.test(formData.name)) {
            toast.error("Please enter a valid name (letters and spaces only, at least 2 characters).");
            return;
        }

        if (!validateEmail(formData.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }

        if (formData.message.trim().length < 10) {
            toast.error("Your message should be at least 10 characters long.");
            return;
        }
        
        if (!recaptchaToken) {
            toast.error("Please complete the reCAPTCHA!");
            return;
        }  
      
        setLoading(true);
        const toastId = toast.loading("Sending your message...");
      
        try {
          const response = await fetch("https://formspree.io/f/mkgovqvl", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({...formData, "g-recaptcha-response": recaptchaToken}),
          });
      
          
          setTimeout(() => {
            if (response.ok) {
              toast.update(toastId, {
                render: "Thank you! Your message has been sent. I will get back to you shortly.",
                type: "success",
                isLoading: false,
                autoClose: 3000,
              });
      
              setFormData({ name: "", email: "", message: "" }); // Reset form
              setRecaptchaToken(null);
              if (recaptchaRef.current) {
                recaptchaRef.current.reset();
              }
            } else {
              toast.update(toastId, {
                render: "Oops! Something went wrong. Please try again later.",
                type: "error",
                isLoading: false,
                autoClose: 3000,
              });
            }
          }, 1000);
        } catch (error) {
          setTimeout(() => {
            toast.update(toastId, {
              render: "Sorry, we couldn't send your message. Please check your connection and try again.",
              type: "error",
              isLoading: false,
              autoClose: 3000,
            });
          }, 1000);
        } finally {
            setLoading(false);
        }
      };
      
  
    return (
      <section className="section px-4 py-10">
        <div className="w-full max-w-lg mx-auto bg-white text-secondary p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-center text-secondary mb-6">
            Contact Me
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
  
            <div>
              <label className="block text-gray-700 font-semibold">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
  
            <div>
              <label className="block text-gray-700 font-semibold">Message</label>
              <textarea
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
            <div className="custom-recaptcha">
                <ReCAPTCHA
                key={key}
                ref={recaptchaRef}
                size={recaptchaSize}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptcha}
                />
            </div>
  
            <button
              type="submit"
              disabled={loading}
              className={`w-full mt-4 text-white py-2 px-6 rounded-full text-lg font-semibold shadow-md transition-transform duration-200 ${
                loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:scale-105"
            }`}>

               {loading ? "Sending..." : "Send Message"}
            </button>
          </form>  
        </div>
      </section>
    );
  };
  
  export default Contact;
  